import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { formatScaled } from "../Shared/scaledDecimal.ts";
import { Coins, MoneyQuestionOptions, Operations, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { withThousands } from "./PlaceValueGenerator.ts";
import { attachMoneyWordProblems } from "./WordProblemGenerator.ts";

/**
 * Money problem generation.
 *
 * Amounts are held and calculated in whole cents throughout - $3.45 is 345 -
 * so the arithmetic is exact integer arithmetic and never accumulates the
 * rounding error that comes of adding 3.45 to 2.18 in binary floating point.
 * They become "$3.45" only on the way to the page, through ProblemData.display.
 *
 * The layout is the ordinary vertical one: a money problem is a column
 * addition that happens to have a decimal point and a dollar sign in it.
 */

/** 345 -> "$3.45". Always two decimal places, so the columns line up. */
export function formatCents(cents: number): string {
    const sign = cents < 0 ? "-" : "";
    return `${sign}$${formatScaled(Math.abs(cents), 2)}`;
}

/**
 * 100000 -> "$1,000.00". `formatCents` with a thousands comma, for sentences:
 * a column of money keeps no comma so its places line up, a price in a
 * sentence reads like one (ADR 0120 §4). Below $1,000.00 the two agree.
 */
export function formatDollars(cents: number): string {
    const sign = cents < 0 ? "-" : "";
    const absolute = Math.abs(cents);
    const dollars = Math.floor(absolute / 100);
    return `${sign}$${withThousands(dollars)}.${String(absolute % 100).padStart(2, "0")}`;
}

/**
 * An amount from one cent up to the limit.
 *
 * Whole dollars are deliberately not excluded: $4.00 - $1.35 is exactly the
 * problem where students have to borrow across two columns at once, and
 * dropping it would remove the hardest and most useful case.
 */
function randomAmount(maxDollars: number): number {
    return getRandomInt(1, Math.max(1, maxDollars * 100 - 1));
}

/**
 * One money problem, or null when the draw is not worth printing - a
 * subtraction of two equal amounts, whose answer is always zero.
 */
export function buildMoneyProblem(
    operator: Operations,
    maxDollars: number,
    key: number
): ProblemData | null {
    if (operator === Operations.Multiplication) {
        // Money times a whole number: four tickets at $3.25 each. Multiplying
        // two amounts of money is not a thing anyone asks a student to do.
        const amount = randomAmount(maxDollars);
        const multiplier = getRandomInt(2, 9);
        const total = amount * multiplier;
        return {
            key,
            numbers: [amount, multiplier, total],
            operator: "x",
            display: [formatCents(amount), String(multiplier), formatCents(total)],
        };
    }

    let first = randomAmount(maxDollars);
    let second = randomAmount(maxDollars);

    if (operator === Operations.Subtraction) {
        if (second > first) {
            [first, second] = [second, first];
        }
        if (first === second) {
            return null;
        }
        const difference = first - second;
        return {
            key,
            numbers: [first, second, difference],
            operator: "-",
            display: [formatCents(first), formatCents(second), formatCents(difference)],
        };
    }

    const total = first + second;
    return {
        key,
        numbers: [first, second, total],
        operator: "+",
        display: [formatCents(first), formatCents(second), formatCents(total)],
    };
}

/** The largest number of coins a counting problem shows. Beyond this it is
 *  arithmetic stamina, not coin recognition (ADR 0018 §4). */
const MAX_COINS = 8;

/**
 * A counting-coins problem: a multiset of coins drawn from the selection, and
 * their sum as the answer. Drawn as coins, not decomposed from an amount, so a
 * page is not all canonical greedy decompositions (ADR 0018 §4).
 */
export function buildCoinCountingProblem(coinNames: string[], key: number): ProblemData {
    const usable = Coins.filter((c) => coinNames.includes(c.name));
    const base = usable.length > 0 ? usable : Coins.slice(0, 4);
    // Weight the pool toward the smaller coins: a uniform draw lands on the
    // greedy (fewest-coins) decomposition of its own total far too often, and a
    // page of those teaches "take the biggest coins" rather than counting.
    // ADR 0018 §4.
    const pool = base.flatMap((coin) => {
        const weight = coin.cents >= 100 ? 1 : coin.cents >= 25 ? 1 : coin.cents >= 10 ? 2 : 3;
        return Array.from({ length: weight }, () => coin);
    });
    const count = getRandomInt(3, MAX_COINS);
    const drawn: string[] = [];
    let total = 0;
    for (let i = 0; i < count; i++) {
        const coin = pool[getRandomInt(0, pool.length - 1)];
        drawn.push(coin.name);
        total += coin.cents;
    }
    // Largest first reads like a pile a child would sort.
    drawn.sort((a, b) => centsOf(b) - centsOf(a));
    return {
        key,
        numbers: [total],
        operator: "",
        display: [formatCents(total)],
        coins: { kind: "counting", coins: drawn, answerText: formatCents(total) },
    };
}

function centsOf(name: string): number {
    return Coins.find((c) => c.name === name)?.cents ?? 0;
}

/**
 * A making-change problem: a price and a plausible amount handed over (the next
 * dollar up, or a round $5 / $10 / $20). The answer is the change (ADR 0018 §5).
 */
export function buildMakingChangeProblem(maxDollars: number, key: number): ProblemData {
    const priceCents = getRandomInt(5, Math.max(6, maxDollars * 100 - 1));
    // The next dollar up is strictly above the price: a $6.00 item handed $6.00
    // has no change to make, and ceil() chose exactly that on whole-dollar prices.
    const dollars = Math.floor(priceCents / 100) + 1;
    const tenders = [dollars * 100];
    for (const round of [500, 1000, 2000, 5000]) {
        if (round > priceCents && round <= Math.max(maxDollars, 20) * 100) {
            tenders.push(round);
        }
    }
    const paidCents = tenders[getRandomInt(0, tenders.length - 1)];
    const changeCents = paidCents - priceCents;
    return {
        key,
        numbers: [priceCents, paidCents, changeCents],
        operator: "",
        display: [formatCents(changeCents)],
        coins: {
            kind: "change",
            coins: [],
            answerText: formatCents(changeCents),
            priceText: formatCents(priceCents),
            paidText: formatCents(paidCents),
        },
    };
}

/** Addition, subtraction and multiplication by a whole number. */
function usableOperators(settings: ISettingsProps): Operations[] {
    const selected = settings.Operators?.map((operator) => operator.value) ?? [];
    const usable = selected.filter(
        (operator) =>
            operator === Operations.Addition ||
            operator === Operations.Subtraction ||
            operator === Operations.Multiplication
    );
    // Dividing money is a real skill but not one this vertical layout serves,
    // so a sheet that asked only for division falls back to addition rather
    // than printing nothing.
    return usable.length > 0 ? usable : [Operations.Addition];
}

/** What counts as the same problem twice, whichever branch built it. */
function fingerprint(problem: ProblemData): string {
    if (problem.coins) {
        return problem.coins.kind === 'counting'
            ? `c:${problem.coins.coins.join(',')}`
            : `x:${problem.coins.priceText}>${problem.coins.paidText}`;
    }
    return problem.display!.slice(0, 2).join(problem.operator);
}

/**
 * A page-shaped set of money problems, matching what GenerateDataV2 returns.
 *
 * Counting-coins/making-change and plain arithmetic are different enough - a
 * different `choices` list, coin kinds versus operators - that this picks its
 * branch once and calls the shared loop from whichever one applies, rather
 * than giving the loop a branch of its own (ADR 0042 §4). Only one branch
 * ever runs for a given sheet, matching the original behaviour exactly.
 */
export function generateMoneyPages(settings: ISettingsProps): ProblemData[][] | null {
    if (!settings.Operators || settings.Operators.length === 0) {
        return null;
    }

    const operators = usableOperators(settings);
    const maxDollars = settings.MoneyMaxDollars ?? 10;
    const question = settings.MoneyQuestion ?? MoneyQuestionOptions.Arithmetic;

    // Which coin question(s) a sheet asks. Mixed alternates counting and change.
    const coinKinds: ('counting' | 'change')[] =
        question === MoneyQuestionOptions.CountingCoins
            ? ['counting']
            : question === MoneyQuestionOptions.MakingChange
            ? ['change']
            : question === MoneyQuestionOptions.Mixed
            ? ['counting', 'change']
            : [];

    if (coinKinds.length > 0) {
        return generateProblemPages({
            settings,
            choices: coinKinds,
            build: (kind, key) =>
                kind === 'counting'
                    ? buildCoinCountingProblem(settings.MoneyCoins ?? [], key)
                    : buildMakingChangeProblem(maxDollars, key),
            fingerprint,
        });
    }

    const pages = generateProblemPages({
        settings,
        choices: operators,
        build: (operator, key) => buildMoneyProblem(operator, maxDollars, key),
        fingerprint,
    });

    // A money word problem is the same arithmetic told as a story, attached
    // after de-duplication so the fingerprint still compares amounts (ADR 0063).
    return question === MoneyQuestionOptions.WordProblems && pages
        ? attachMoneyWordProblems(pages)
        : pages;
}
